import constate from "constate";
import { useCallback, useState } from "react";

const IMPERSONATION_STORAGE_KEY = "cbh:impersonation";

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedBy: string;
  cognitoUsername: string;
  accessToken: string;
}

interface StartImpersonationParams {
  impersonatedBy: string;
  cognitoUsername: string;
  accessToken: string;
}

function loadPersistedState(): ImpersonationState {
  try {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as ImpersonationState;
    }
  } catch {
    // Ignore corrupted storage
  }

  return {
    isImpersonating: false,
    impersonatedBy: "",
    cognitoUsername: "",
    accessToken: "",
  };
}

function useImpersonationState() {
  const [state, setState] = useState<ImpersonationState>(loadPersistedState);

  const startImpersonation = useCallback((params: StartImpersonationParams) => {
    const newState: ImpersonationState = {
      isImpersonating: true,
      impersonatedBy: params.impersonatedBy,
      cognitoUsername: params.cognitoUsername,
      accessToken: params.accessToken,
    };
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(newState));
    setState(newState);
  }, []);

  const endImpersonation = useCallback(() => {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setState({
      isImpersonating: false,
      impersonatedBy: "",
      cognitoUsername: "",
      accessToken: "",
    });
  }, []);

  return { ...state, startImpersonation, endImpersonation };
}

const [ImpersonationProvider, useImpersonation] = constate(useImpersonationState);

export { ImpersonationProvider, useImpersonation };
